import * as Sentry from "@sentry/react";
import { SPAN_STATUS_ERROR, SPAN_STATUS_OK } from "@sentry/core";

const SESSION_ID_KEY = "expedibox.session_id";

type MetricAttributes = Record<string, string>;
type FlowResult = "success" | "failure";

const toMetricAttributes = (
  attributes?: Record<string, string | number | boolean>
): MetricAttributes | undefined => {
  if (!attributes) return undefined;
  return Object.entries(attributes).reduce<MetricAttributes>((acc, [key, value]) => {
    acc[key] = String(value);
    return acc;
  }, {});
};

const isMetricsEnabled = () => {
  const client = Sentry.getClient();
  return Boolean(client?.getOptions()?.enableMetrics);
};

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

export const getSessionId = () => {
  if (typeof window === "undefined") {
    return "unknown";
  }

  try {
    const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const next = crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.sessionStorage.setItem(SESSION_ID_KEY, next);
    return next;
  } catch {
    return "unknown";
  }
};

export const addBreadcrumb = (
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: "info" | "warning" | "error" = "info"
) => {
  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data: {
      sessionId: getSessionId(),
      ...data,
    },
  });
};

export const recordFlowMetrics = (flow: string, result: FlowResult, startedAt?: number) => {
  if (!isMetricsEnabled()) return;
  const attributes = toMetricAttributes({ flow, result });
  Sentry.metrics.count("locker.flow.count", 1, { attributes });

  if (typeof startedAt === "number") {
    const durationMs = Math.max(0, Math.round(now() - startedAt));
    Sentry.metrics.distribution("locker.flow.duration", durationMs, { attributes });
  }
};

export const recordGauge = (
  name: string,
  value: number,
  attributes?: Record<string, string | number | boolean>
) => {
  if (!isMetricsEnabled()) return;
  Sentry.metrics.gauge(name, value, { attributes: toMetricAttributes(attributes) });
};

export const recordDistribution = (
  name: string,
  value: number,
  attributes?: Record<string, string | number | boolean>
) => {
  if (!isMetricsEnabled()) return;
  Sentry.metrics.distribution(name, value, { attributes: toMetricAttributes(attributes) });
};

export const recordIncrement = (
  name: string,
  value = 1,
  attributes?: Record<string, string | number | boolean>
) => {
  if (!isMetricsEnabled()) return;
  Sentry.metrics.count(name, value, { attributes: toMetricAttributes(attributes) });
};

export const withSpan = async <T>(
  context: Parameters<typeof Sentry.startSpan>[0],
  fn: (span?: Sentry.Span) => Promise<T>
): Promise<T> =>
  Sentry.startSpan(context, async (span) => {
    try {
      const result = await fn(span);
      span?.setStatus({ code: SPAN_STATUS_OK });
      return result;
    } catch (error) {
      span?.setStatus({ code: SPAN_STATUS_ERROR, message: "internal_error" });
      throw error;
    }
  });

export const startTimer = () => now();
